<script lang="ts" setup>
import { reactive, ref, computed, watch, onUnmounted, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { jwtDecode, type JwtPayload } from "jwt-decode";

import { ConfirmDialog, Modal, Button } from "@/components/global";

import { PUBLIC_AUTH_ROUTE_NAMES } from "@/router";
import TokenService from "@/api.resource/token.service";

const store = useStore();
const route = useRoute();

const MCAdminCognitoID = computed<string>(() => store.getters["auth/MCAdminCognitoID"]);
const userMail = computed<string>(() => store.getters["auth/userMail"]);
const accessToken = computed<string | null>(() => store.getters["auth/accessToken"]);
const isLoggedIn = computed(() => !!TokenService.getAccessToken() || !!accessToken.value);
const isPublicAuthRoute = computed(() => PUBLIC_AUTH_ROUTE_NAMES.includes(route.name as string));
const isTimeoutTrackingEnabled = computed(() => isLoggedIn.value && !isPublicAuthRoute.value);

const checkIdleState = reactive<{
  isIdle: boolean;
  timeoutId: ReturnType<typeof setTimeout> | null;
  idleTimeout: number;
}>({
  isIdle: false,
  timeoutId: null,
  idleTimeout: 1000 * 60 * 15
});

const showIdleDialog = ref(false);
const showTokenDialog = ref(false);
const showTimeoutModal = ref(false);
const timeoutModalEligible = ref(false);
const timeoutReason = ref<"inactivity" | "expired" | null>(null);
const isRenewingSession = ref(false);
const isLoggingOutSession = ref(false);

const idleCountdown = ref(60);
const tokenCountdown = ref(60);

const idleWarningDuration = 1000 * 60;
const tokenWarningDuration = 1000 * 60;

const idleWarningTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const idleLogoutTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const idleCountdownIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

const accessTokenExpiryTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const tokenWarningTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const tokenExpiryTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const tokenCountdownIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

const formatSeconds = (seconds: number) => {
  const safeSeconds = Math.max(seconds, 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const idleDialogSubMessage = computed(() => `Time remaining: ${formatSeconds(idleCountdown.value)}`);

const tokenDialogSubMessage = computed(() => `Time remaining: ${formatSeconds(tokenCountdown.value)}`);
const isTokenActionLoading = computed(() => isRenewingSession.value || isLoggingOutSession.value);
const isIdleActionLoading = computed(() => isLoggingOutSession.value);

const timeoutMessage = computed(() => {
  if (timeoutReason.value === "expired") {
    return "You've been logged out due to expired session.";
  }
  return "You've been logged out due to inactivity.";
});

const clearIdleTimers = () => {
  if (checkIdleState.timeoutId) {
    clearTimeout(checkIdleState.timeoutId);
    checkIdleState.timeoutId = null;
  }

  if (idleWarningTimeoutId.value) {
    clearTimeout(idleWarningTimeoutId.value);
    idleWarningTimeoutId.value = null;
  }

  if (idleLogoutTimeoutId.value) {
    clearTimeout(idleLogoutTimeoutId.value);
    idleLogoutTimeoutId.value = null;
  }

  if (idleCountdownIntervalId.value) {
    clearInterval(idleCountdownIntervalId.value);
    idleCountdownIntervalId.value = null;
  }
};

const startIdleCountdown = (seconds: number) => {
  idleCountdown.value = seconds;
  showIdleDialog.value = true;

  if (idleCountdownIntervalId.value) {
    clearInterval(idleCountdownIntervalId.value);
  }

  idleCountdownIntervalId.value = setInterval(() => {
    idleCountdown.value -= 1;
    if (idleCountdown.value <= 0) {
      if (idleCountdownIntervalId.value) {
        clearInterval(idleCountdownIntervalId.value);
        idleCountdownIntervalId.value = null;
      }
    }
  }, 1000);
};

const resetTimer = () => {
  if (!isTimeoutTrackingEnabled.value) return;
  clearIdleTimers();
  showIdleDialog.value = false;
  checkIdleState.isIdle = false;

  const timeUntilWarning = checkIdleState.idleTimeout - idleWarningDuration;
  const warningDelay = Math.max(timeUntilWarning, 0);

  idleWarningTimeoutId.value = setTimeout(() => {
    checkIdleState.isIdle = true;
    const countdownSeconds = Math.ceil(Math.min(idleWarningDuration, checkIdleState.idleTimeout) / 1000);
    startIdleCountdown(countdownSeconds);
  }, warningDelay);

  idleLogoutTimeoutId.value = setTimeout(() => {
    handleAutoLogout("inactivity");
  }, checkIdleState.idleTimeout);
};

const onActivity = () => {
  if (!isTimeoutTrackingEnabled.value) return;
  if (showIdleDialog.value || showTokenDialog.value || showTimeoutModal.value) return;
  checkIdleState.isIdle = false;
  resetTimer();
};

const decodeTokenExp = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const decodedToken = jwtDecode<JwtPayload>(token);
    if (!decodedToken.exp) return null;
    return decodedToken.exp * 1000;
  } catch {
    return null;
  }
};

const clearTokenTimers = () => {
  if (accessTokenExpiryTimeout.value) {
    clearTimeout(accessTokenExpiryTimeout.value);
    accessTokenExpiryTimeout.value = null;
  }

  if (tokenWarningTimeoutId.value) {
    clearTimeout(tokenWarningTimeoutId.value);
    tokenWarningTimeoutId.value = null;
  }

  if (tokenExpiryTimeoutId.value) {
    clearTimeout(tokenExpiryTimeoutId.value);
    tokenExpiryTimeoutId.value = null;
  }

  if (tokenCountdownIntervalId.value) {
    clearInterval(tokenCountdownIntervalId.value);
    tokenCountdownIntervalId.value = null;
  }
};

const startTokenCountdown = (seconds: number) => {
  tokenCountdown.value = Math.max(seconds, 0);
  showTokenDialog.value = true;

  if (tokenCountdownIntervalId.value) {
    clearInterval(tokenCountdownIntervalId.value);
  }

  tokenCountdownIntervalId.value = setInterval(() => {
    tokenCountdown.value -= 1;
    if (tokenCountdown.value <= 0) {
      tokenCountdown.value = 0;
      if (tokenCountdownIntervalId.value) {
        clearInterval(tokenCountdownIntervalId.value);
        tokenCountdownIntervalId.value = null;
      }
    }
  }, 1000);
};

const handleAccessTokenExpiry = async () => {
  if (!isTimeoutTrackingEnabled.value) return;

  const refreshToken = TokenService.getRefreshToken();
  const refreshTokenExp = decodeTokenExp(refreshToken);

  if (!refreshTokenExp || refreshTokenExp <= Date.now()) {
    clearTokenTimers();
    const countdownSeconds = Math.ceil(tokenWarningDuration / 1000);
    startTokenCountdown(countdownSeconds);
    tokenExpiryTimeoutId.value = setTimeout(() => {
      handleAutoLogout("expired");
    }, countdownSeconds * 1000);
    return;
  }

  try {
    await store.dispatch("auth/REFRESH_TOKEN");
  } catch {
    const remainingMs = refreshTokenExp - Date.now();
    if (remainingMs <= 0) {
      handleAutoLogout("expired");
      return;
    }

    clearTokenTimers();
    const countdownSeconds = Math.ceil(Math.min(tokenWarningDuration, remainingMs) / 1000);
    startTokenCountdown(countdownSeconds);
    tokenExpiryTimeoutId.value = setTimeout(() => {
      handleAutoLogout("expired");
    }, remainingMs);
  }
};

const handleIdleStaySignedIn = () => {
  if (isIdleActionLoading.value) return;
  showIdleDialog.value = false;
  checkIdleState.isIdle = false;
  resetTimer();
};

const handleIdleSignOut = async () => {
  if (isIdleActionLoading.value) return;
  isLoggingOutSession.value = true;
  showIdleDialog.value = false;
  timeoutModalEligible.value = false;
  showTimeoutModal.value = false;

  try {
    await store.dispatch("auth/LOGOUT");
  } finally {
    isLoggingOutSession.value = false;
  }
};

const handleTokenRenew = async () => {
  if (isTokenActionLoading.value) return;
  isRenewingSession.value = true;

  try {
    await store.dispatch("auth/REFRESH_TOKEN");
    showTokenDialog.value = false;
  } catch {
    handleAutoLogout("expired");
  } finally {
    isRenewingSession.value = false;
  }
};

const handleTokenSignOut = async () => {
  if (isTokenActionLoading.value) return;
  isLoggingOutSession.value = true;
  showTokenDialog.value = false;
  timeoutModalEligible.value = false;
  showTimeoutModal.value = false;

  try {
    await store.dispatch("auth/LOGOUT");
  } finally {
    isLoggingOutSession.value = false;
  }
};

const handleTimeoutModalClose = () => {
  showTimeoutModal.value = false;
  timeoutModalEligible.value = false;
  window.location.href = "/login";
};

const handleAutoLogout = async (reason: "inactivity" | "expired") => {
  if (!isTimeoutTrackingEnabled.value) return;
  isRenewingSession.value = false;
  isLoggingOutSession.value = false;
  clearIdleTimers();
  clearTokenTimers();
  showIdleDialog.value = false;
  showTokenDialog.value = false;
  timeoutModalEligible.value = true;
  showTimeoutModal.value = true;
  timeoutReason.value = reason;

  await store.dispatch("auth/LOGOUT", { redirect: false, force: true });
};

watch(() => accessToken.value, value => {
  clearTokenTimers();
  if (!isTimeoutTrackingEnabled.value) {
    showTokenDialog.value = false;
    return;
  }

  if (value) {
    const accessTokenExp = decodeTokenExp(value);
    if (accessTokenExp && accessTokenExp > Date.now()) {
      accessTokenExpiryTimeout.value = setTimeout(() => {
        handleAccessTokenExpiry();
      }, accessTokenExp - Date.now());
    }

    const refreshToken = TokenService.getRefreshToken();
    const refreshTokenExp = decodeTokenExp(refreshToken);
    if (refreshTokenExp && refreshTokenExp > Date.now()) {
      const timeUntilExpiry = refreshTokenExp - Date.now();
      const warningDelay = Math.max(timeUntilExpiry - tokenWarningDuration, 0);
      tokenWarningTimeoutId.value = setTimeout(() => {
        const countdownSeconds = Math.ceil(Math.min(tokenWarningDuration, timeUntilExpiry) / 1000);
        startTokenCountdown(countdownSeconds);
      }, warningDelay);

      tokenExpiryTimeoutId.value = setTimeout(() => {
        handleAutoLogout("expired");
      }, timeUntilExpiry);
    } else if (refreshTokenExp && refreshTokenExp <= Date.now()) {
      handleAutoLogout("expired");
    }
  }
}, { immediate: true, deep: true });

watch(() => isTimeoutTrackingEnabled.value, enabled => {
  if (enabled) {
    resetTimer();
    return;
  }

  isRenewingSession.value = false;
  isLoggingOutSession.value = false;

  clearIdleTimers();
  clearTokenTimers();

  showIdleDialog.value = false;
  showTokenDialog.value = false;

  if (!timeoutModalEligible.value) {
    showTimeoutModal.value = false;
    timeoutReason.value = null;
  }
}, { immediate: true });

watch(() => checkIdleState.isIdle, value => {
  if (isTimeoutTrackingEnabled.value && MCAdminCognitoID.value !== "" && userMail.value !== "" && value) {
    showIdleDialog.value = true;
  }
});

onMounted(() => {
  if (isTimeoutTrackingEnabled.value) {
    resetTimer();
  }

  window.addEventListener("mousemove", onActivity);
  window.addEventListener("keydown", onActivity);

  store.dispatch("settings/USER_AGREEMENT_HTML_STRING", "HTML");
});

onUnmounted(() => {
  clearIdleTimers();
  clearTokenTimers();

  window.removeEventListener("mousemove", onActivity);
  window.removeEventListener("keydown", onActivity);
});
</script>

<template>
  <router-view />

  <div class="session-overlays">
    <ConfirmDialog
      v-if="showIdleDialog"
      title="You will be logged out soon"
      message="For your security, we log you out automatically when you have been inactive for a certain period of time."
      :subMessage="idleDialogSubMessage"
      cancelText="Log out now"
      confirmText="Continue session"
      confirmButtonVariant="default"
      cancelButtonVariant="outline"
      :cancelLoading="isLoggingOutSession"
      :confirmDisabled="isIdleActionLoading"
      :cancelDisabled="isIdleActionLoading"
      @cancelModal="handleIdleSignOut"
      @confirm="handleIdleStaySignedIn"
    />

    <ConfirmDialog
      v-if="showTokenDialog"
      title="Your current session will expire soon"
      message="To keep your account secure, your session will expire soon. Renew now to stay signed in."
      :subMessage="tokenDialogSubMessage"
      cancelText="Log out now"
      confirmText="Renew session"
      confirmButtonVariant="default"
      cancelButtonVariant="outline"
      :confirmLoading="isRenewingSession"
      :cancelLoading="isLoggingOutSession"
      :confirmDisabled="isTokenActionLoading"
      :cancelDisabled="isTokenActionLoading"
      @cancelModal="handleTokenSignOut"
      @confirm="handleTokenRenew"
    />

    <Modal
      :showModal="showTimeoutModal && timeoutModalEligible"
      :closable="false"
      :rounded="true"
      maxWidth="28rem"
      @closeModal="handleTimeoutModalClose"
    >
      <div class="p-4 text-center">
        <h4 class="mb-2">Session Timeout!</h4>
        <p class="mb-4">{{ timeoutMessage }}</p>

        <div class="flex-center">
          <Button
            size="sm"
            @click="handleTimeoutModalClose"
          >
            Ok
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style>
:root {
  --ch-white: #ffffff;
  --ch-black: #000000;
  --ch-black-5: #0000000D;
  --ch-black-10: #0000001A;
  --ch-black-20: #00000033;
  --ch-black-30: #0000004d;
  --ch-black-40: #00000066;
  --ch-black-50: #00000080;
  --ch-black-70: #000000B3;
  --ch-black-80: #000000CC;
  --ch-text-head: #2c70b7;
  --ch-dashboard-bg: #f8f8f8;
  --ch-blue-10: #1b3c771a;
  --ch-blue-100: #1b3c77;
  --ch-blue-200: #1c5691;
  --ch-blue-300: #1a436e;
  --ch-blue-400: #245f9c;
  --ch-soft-blue: #bbc4d6;
  --ch-light-blue: #e2edf9;
  --ch-light-blue-50: #e2edf980;
  --ch-sky-blue: #d1dde9;
  --ch-sky-blue-50: #d1dde980;
  --ch-secondary-light: #e6f9dc;
  --ch-secondary: #6cbd45;
  --ch-dark-grey: #4b4b4b;
  --ch-light-grey: #e8e8e8;
  --ch-border-grey: #8e9191;
  --ch-border-grey-30: #8e91914d;
  --ch-input: #bfd3e9;
  --ch-active: #18a34b;
  --ch-inactive: #bd4545;
  --ch-warning: #af3914;
  --ch-danger: #e60a0c;
  --ch-sub-black: #000000b3;
  --ch-table-row: #fbfbfb;
  --ch-avatar-bg: #f2f2f7;
  --ch-readonly: #9999991f;
  --ch-navy-blue: #1c569105;
  --ch-pill: #fbe5e5;

  --font-5xl: 2.5rem;
  --font-4xl: 2.25rem;
  --font-3xl: 2rem;
  --font-2xl: 1.75rem;
  --font-xl: 1.5rem;
  --font-lg: 1.25rem;
  --font-base: 1rem;
  --font-sm: 0.875rem;
  --font-xs: 0.75rem;
  --font-xxs: 0.625rem;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  -webkit-box-sizing: border-box;
}

#app {
  font-family: "DM Sans", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

button {
  background: none;
  border: none;
  outline: none;
}

button[disabled] {
  cursor: not-allowed;
}

input[disabled] {
  cursor: not-allowed;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px rgba(28, 86, 145, 0.05) inset !important;
  transition: background-color 5000s ease-in-out 0s !important;
}

a {
  text-decoration: none;
}

h1,
h2,
h3,
h4,
h5,
h6,
p {
  margin: 0;
}

/* utility classes */
.flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* global classes */
.form-error {
  color: var(--ch-danger);
  font-size: 0.85rem;
}

.v-toast {
  z-index: 1100 !important;
}

.session-overlays {
  position: relative;
  z-index: 1500;
}

.ellipsis-wrapper {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fixed-submit-wrapper {
  padding: 1.25rem 1rem;
  width: 100%;
  height: 5rem;
  position: absolute;
  bottom: 0;
  background: var(--ch-white);
  border-radius: 0 0 10px 10px;
  box-shadow: 0px 10px 30px 0px #2C70B740;
}

.spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid var(--ch-border-grey);
  border-top-color: var(--ch-blue-100);
  border-radius: 100%;
  animation: spin 0.8s linear infinite;
}

.spinner-small {
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--ch-border-grey);
  border-top-color: var(--ch-blue-100);
  border-radius: 100%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* table classes */
.table-content-wrapper {
  position: relative;
  margin: 2.25rem 0 1.25rem 0;
}

.table-container {
  position: relative;
  padding: 1.25rem 1.5rem;
  width: 100%;
  height: 100%;
  border: 0.5px solid #0000004D;
  border-radius: 0.625rem;
}

.table-action-btn {
  position: absolute;
  right: -0.75rem;
  bottom: -0.625rem;
  width: 100%;
  height: 100%;
  max-width: 3.5rem;
  max-height: 3.5rem;
  box-shadow: 0px 4px 4px 0px #00000040;
}

/* popover styles */
.popover-panel {
  position: absolute;
  z-index: 10;
  background-color: var(--ch-white);
  border-radius: 5px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
}

.panel-left {
  left: 0;
  top: 0;
  transform: translateX(calc(-100% + 0.75rem));
}

.panel-right {
  right: 0;
  top: 0;
  transform: translateX(calc(100% - 2.25rem));
}

h1.settings-tab-header {
  color: var(--ch-blue-100);
  font-size: var(--font-lg);
  font-weight: 400;
  margin: 1.5rem auto;
  text-align: center;
}

.card-brand-loader-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: var(--ch-black-10);
  z-index: 50;
  border-radius: 10px;
}
</style>
